import { useMemo } from "react";
import { useInfiniteQuery, keepPreviousData } from "@tanstack/react-query";
import { dealApi, dealQueryKeys } from "service/crm/api/deal";
import { DealStage, GetDealsParams } from "types/crm/deal";
import { StageCounts } from "types/crm/pagination";

export const useDealsQuery = (
  params: Omit<GetDealsParams, "page"> = {},
  options?: {
    enabled?: boolean;
    /**
     * First page to request. A board lane starts at 1 because page 0 already
     * came with GET /crm/deals/kanban.
     */
    startPage?: number;
  },
) => {
  const startPage = options?.startPage ?? 0;

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: startPage
      ? [...dealQueryKeys.list(params), { startPage }]
      : dealQueryKeys.list(params),
    queryFn: ({ pageParam }) => dealApi.getDeals({ ...params, page: pageParam }),
    initialPageParam: startPage,
    getNextPageParam: (lastPage) =>
      lastPage.last ? undefined : lastPage.page + 1,
    enabled: options?.enabled ?? true,
    // A lane's later pages must never show a previous search's cards while loading.
    placeholderData: startPage ? undefined : keepPreviousData,
  });

  const { deals, totalElements, stageCounts } = useMemo(
    () => ({
      deals: data?.pages.flatMap((p) => p?.content ?? []) ?? [],
      totalElements: data?.pages[0]?.totalElements ?? 0,
      stageCounts: (data?.pages[0]?.counts ?? {}) as StageCounts<DealStage>,
    }),
    [data],
  );

  return {
    deals,
    isLoading,
    isFetchingMore: isFetchingNextPage,
    hasMore: !!hasNextPage,
    onLoadMore: fetchNextPage,
    totalElements,
    stageCounts,
  };
};
